import { Input, InputNumber, message, Modal, Select } from "antd";
import React, { useEffect, useState } from "react";
import {
  useCountryStruct,
  useCreateWarehouse,
  useGetWarehouseById,
  useUpdateWarehouse,
} from "../../api/admin";

type CreateWareHouseModalProps = {
  open: boolean;
  selectedId?: number;
  hideModal: () => void;
};

export type Woreda = {
  woredaId: number;
  woredaName: string;
};

export type Zone = {
  zoneId: number;
  zoneName: string;
};

export type Region = {
  regionId: number;
  regionName: string;
};

const CreateWareHouseModal: React.FC<CreateWareHouseModalProps> = ({
  open,
  selectedId,
  hideModal,
}) => {
  const { data } = useCountryStruct();
  const { mutateAsync: createCenter, isPending } = useCreateWarehouse();
  const { mutateAsync: updateCenter } = useUpdateWarehouse();
  const { data: selectedWarehouse, refetch } = useGetWarehouseById(selectedId);
  const [countryId, setCountryId] = useState(-1);

  const [regions, setRegions] = useState<Region[] | undefined>();
  const [selectedRegionId, setSelectedRegionId] = useState<
    number | undefined
  >();

  const [zones, setZones] = useState<Zone[] | undefined>();
  const [selectedZoneId, setSelectedZoneId] = useState<number | undefined>();

  const [woredas, setWoredas] = useState<Woreda[] | undefined>();
  const [selectedWoredaId, setSelectedWoredaId] = useState<
    number | undefined
  >();

  const [warehouseName, setWarehouseName] = useState<string>();
  const [capacity, setCapacity] = useState<number | null>();

  useEffect(() => {
    if (selectedId) {
      refetch();
    } else {
      resetForm();
    }
  }, [selectedId]);

  useEffect(() => {
    if (
      selectedWarehouse &&
      selectedWarehouse.data &&
      selectedWarehouse.data.data
    ) {
      setCapacity(selectedWarehouse.data.data.capacity);
      setWarehouseName(selectedWarehouse.data.data.centerName);

      if (selectedWarehouse.data.data.location) {
        const [_country, _region, _zone, _woreda] =
          selectedWarehouse.data.data.location.split("/ ");
        if (data && data.data && regions && zones && woredas) {
          setCountryId(data.data.countryId);

          const rgn = regions.find(
            (item: Region) => item.regionName === _region
          );

          if (rgn) {
            setSelectedRegionId(rgn.regionId);
          }
          const zn = zones?.find((item: Zone) => item.zoneName === _zone);

          if (zn) {
            setSelectedZoneId(zn.zoneId);
          }

          const wrd = woredas?.find(
            (item: Woreda) => item.woredaName === _woreda
          );

          if (wrd) {
            setSelectedWoredaId(wrd.woredaId);
          }
        }
      }
    }
  }, [selectedWarehouse]);

  useEffect(() => {
    if (data && data.data) {
      setCountryId(data.data.data.countryId);

      const _regions = data.data.data.regionDtoList.map((item: any) => {
        return { regionId: item.regionId, regionName: item.regionName };
      });

      setRegions(_regions);
    }
  }, [data]);

  useEffect(() => {
    if (data && data.data && selectedRegionId) {
      const region = data.data.data.regionDtoList.find(
        (r: any) => r.regionId === selectedRegionId
      );

      if (region) {
        const _zones = region.zoneDtos.map((item: any) => {
          return { zoneId: item.zoneId, zoneName: item.zoneName };
        });

        setZones(_zones);
      }
    }
  }, [selectedRegionId]);

  useEffect(() => {
    if (
      data &&
      data.data &&
      data.data.data &&
      selectedRegionId &&
      selectedZoneId
    ) {
      const _region = data.data.data.regionDtoList.find(
        (r: any) => r.regionId === selectedRegionId
      );

      if (_region) {
        const _zones = _region.zoneDtos.map((z: any) => {
          return {
            zoneId: z.zoneId,
            zoneName: z.zoneName,
            woredas: z.woredaDtos,
          };
        });

        const _zone = _zones.find((z: Zone) => z.zoneId === selectedZoneId);

        if (_zone && _zone.woredas) {
          const _woredas = _zone.woredas.map((w: any) => {
            return { woredaId: w.woredaId, woredaName: w.woredaName };
          });
          setWoredas(_woredas);
        }
      }
    }
  }, [selectedZoneId]);

  const onsubmit = async () => {
    if (selectedId && selectedWoredaId && capacity && warehouseName) {
      try {
        const data = {
          id: selectedId,
          warehouse: {
            centerName: warehouseName,
            capacity: capacity,
            woredaId: selectedWoredaId,
          },
        };

        const result = await updateCenter(data);
        message.success("Warehouse updated successfully.");
      } catch (err: any) {
        message.error("Operation failed");
      } finally {
        hideModal();
      }
    } else {
      try {
        if (selectedWoredaId && capacity && warehouseName) {
          const data = {
            centerName: warehouseName,
            capacity: capacity,
            woredaId: selectedWoredaId,
          };

          const result = await createCenter(data);
          message.success("Warehouse created successfully.");
        }
      } catch (err: any) {
        message.error("Operation failed");
      } finally {
        hideModal();
      }
    }
  };

  const resetForm = () => {
    setSelectedRegionId(undefined);
    setSelectedZoneId(undefined);
    setSelectedWoredaId(undefined);
    setCapacity(undefined);
    setWarehouseName(undefined);
  };
  return (
    <Modal
      afterClose={resetForm}
      title={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: 27.43,
            color: "black",
          }}
        >
          {selectedId ? "Edit Center" : "Create Center"}
        </div>
      }
      open={open}
      centered
      onOk={onsubmit}
      okText={isPending ? "Saving..." : "Ok"}
      okButtonProps={{ disabled: isPending }}
      onCancel={hideModal}
      closable
    >
      <div
        style={{
          fontFamily: "Poppins",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <Select
          value={selectedRegionId}
          placeholder="Select region"
          style={{ width: "100%" }}
          onChange={(val: any) => setSelectedRegionId(Number(val))}
          options={regions?.map((item: any) => {
            return { label: item.regionName, value: item.regionId };
          })}
        />

        <Select
          value={selectedZoneId}
          placeholder="Select zone"
          style={{ width: "100%" }}
          onChange={(val: any) => setSelectedZoneId(Number(val))}
          options={zones?.map((item: any) => {
            return { label: item.zoneName, value: item.zoneId };
          })}
        />

        <Select
          value={selectedWoredaId}
          placeholder="Select woreda"
          style={{ width: "100%" }}
          onChange={(val: any) => setSelectedWoredaId(Number(val))}
          options={woredas?.map((item: any) => {
            return { label: item.woredaName, value: item.woredaId };
          })}
        />
        <Input
          placeholder="Insert warehouse name"
          value={warehouseName}
          onChange={(e) => setWarehouseName(e.target.value)}
        />
        <InputNumber
          addonBefore={"Kg"}
          style={{ width: "100%" }}
          placeholder="Insert warehouse capacity"
          value={capacity}
          onChange={(value) => setCapacity(value)}
        />
      </div>
    </Modal>
  );
};

export default CreateWareHouseModal;
